export const TAP_TARGET = "pointer-coarse:min-h-10";
export const TAP_TARGET_INLINE = "pointer-coarse:py-1";
export const TAP_AREA =
  "relative pointer-coarse:after:absolute pointer-coarse:after:top-1/2 pointer-coarse:after:left-1/2 pointer-coarse:after:h-10 pointer-coarse:after:w-[max(100%,2.5rem)] pointer-coarse:after:-translate-1/2";

export const PRESS =
  "transition-[color,background-color,border-color,opacity] duration-120 active:duration-0 active:brightness-95";
export const PRESS_ROW =
  "transition-colors duration-120 active:duration-0 active:not-disabled:not-data-disabled:bg-surface-2";
export const PRESS_TILE =
  "transition-colors duration-120 has-[[data-hit]:active]:bg-surface-2 has-[[data-hit]:active]:duration-0";

export const FOCUS_RING =
  "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus";
export const FOCUS_RING_INSET =
  "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus";

export const SORT_HEAD = `inline-flex min-h-6 max-w-full items-center gap-1 transition-colors duration-120 hover:text-ink-1 ${FOCUS_RING}`;

export const CONTROL_OUTLINE = "border border-edge-strong bg-raised hover:bg-raised-hover";

export const CONTROL_HEIGHT = "h-7.5 pointer-coarse:h-10";
export const CONTROL_SQUARE = "size-7.5 pointer-coarse:size-10";
export const CONTROL_WIDTH = "w-64 max-sm:w-full";

const CONTROL_FRAME_SHAPE = `rounded-control border text-base transition-colors duration-120 ${FOCUS_RING} disabled:cursor-not-allowed disabled:opacity-50`;
export const CONTROL_FRAME = `${CONTROL_HEIGHT} ${CONTROL_FRAME_SHAPE} bg-raised`;
export const CONTROL_AREA = `${CONTROL_FRAME_SHAPE} bg-raised`;

export const PAGE_FRAME = "mx-auto w-full max-w-350";
export const FORM_COLUMN = "w-full max-w-4xl";

export const ROW_FRAME = "min-h-16 px-5";
export const ROW = `flex flex-col justify-center ${ROW_FRAME} py-2.5`;
export const ROW_LABEL = "text-md font-medium text-balance text-ink-1";
export const ROW_HINT = "text-sm leading-snug text-ink-2";
export const ROW_LINE = "flex items-center justify-between gap-x-4 gap-y-3 @max-2xs:flex-wrap";
export const ROW_MAIN = "flex min-w-24 flex-1 basis-0 items-center gap-3";
export const ROW_LEAD = "grid h-7 min-w-7 shrink-0 place-items-center";
export const ROW_END =
  "flex min-h-7.5 min-w-0 flex-wrap items-center justify-end gap-2 @max-2xs:w-full @max-2xs:justify-start";

export const TAB_HEIGHT = "h-9";

export const PAGE_TITLE = "text-2xl font-semibold text-ink-1";
export const BAR_LINE = "text-bar leading-10 max-md:text-xl";
export const PHRASE_TITLE = "text-lg leading-7 sm:text-xl sm:leading-10 md:text-bar";
export const PAGE_MAIN =
  "scrollbar-subtle scrollbar-stable relative min-w-0 px-4 pb-10 md:min-h-0 md:overflow-y-auto md:px-6";

export const CARD_SURFACE = "rounded-card border border-edge bg-surface-1";
export const GROUP_LABEL = "text-sm font-medium text-ink-2";
export const SECTION_TITLE = "text-xl font-semibold text-ink-1";
export const PANE_SECTION_TITLE = "text-xs font-semibold tracking-wide text-ink-3 uppercase";
export const CARD_QUIET = "rounded-card bg-surface-1";
export const CARD_PAD = "px-5 py-4";
export const TILE_PAD = "p-4";

export const DISPLAY =
  "text-[clamp(2.5rem,6.5vw,4.75rem)] leading-[0.95] font-extrabold tracking-[-0.045em]";
export const DISPLAY_HERO =
  "text-[clamp(2.75rem,9.5vw,7rem)] leading-[0.88] font-extrabold tracking-[-0.05em]";
export const PRODUCT_HERO =
  "text-[clamp(2.75rem,6vw,4.5rem)] leading-none font-semibold tracking-[-0.05em] text-balance";
export const PRODUCT_TITLE =
  "text-[clamp(2.25rem,4.4vw,3.5rem)] leading-none font-semibold tracking-[-0.05em] text-balance";

const ENTRANCE_GUTTER = "px-[clamp(1.25rem,4vw,3rem)]";
export const ENTRANCE_FRAME = `mx-auto w-full max-w-[87.5rem] ${ENTRANCE_GUTTER}`;
export const TOP_BAR_INSET = "pl-3 pr-4";

export const TOP_BAR_PX = 56;
export const ENTRANCE_BAR_OFFSET = "top-20";
export const TOP_BAR_FRAME = "sticky top-0 z-40 flex h-14 shrink-0 items-center";
export const TOP_BAR_OFFSET = "top-14";

export const RAIL_WIDE = "md:w-57";
export const RAIL_COLLAPSED = "md:w-14";
export const railWidth = (wide: boolean): string => (wide ? RAIL_WIDE : RAIL_COLLAPSED);

export const PHRASE_WORD =
  "cursor-pointer rounded-control px-1 underline decoration-1 underline-offset-4 transition-colors duration-120 box-decoration-clone";
export const PHRASE_WORD_IDLE = "text-ink-1 decoration-edge-strong hover:decoration-ink-2";

export const PROPERTY_ROW =
  "grid min-h-7.5 grid-cols-[var(--property-label,5.5rem)_minmax(0,1fr)] items-center gap-x-3";
export const PROPERTY_LABEL = "truncate text-sm text-ink-3";
export const PROPERTY_GROUP = "text-xs font-medium tracking-wide text-ink-3 uppercase";

export const DIALOG_INSET = "px-6 sm:px-7";
export const DIALOG_BLEED = "-mx-6 sm:-mx-7";

export const POPOVER_FIT = "w-[min(20rem,calc(100vw-1rem))]";
export const POPOVER_WIDE = "w-[min(30rem,calc(100vw-1rem))]";
export const POPOVER_TALL = "max-h-[75vh]";
export const POPOVER_SURFACE = "rounded-card border border-edge bg-raised shadow-menu";
export const MENU_SURFACE = `z-40 ${POPOVER_SURFACE}`;
export const MENU_LIST = "flex flex-col gap-px p-1";
export const MENU_ROW_FRAME = `flex h-7 w-full shrink-0 items-center gap-2 rounded-control px-2 text-left text-base ${PRESS_ROW} pointer-coarse:h-10`;
export const MENU_ROW_LIT =
  "outline-none data-lit:bg-raised-hover data-highlighted:bg-raised-hover";
export const MENU_ROW = `${MENU_ROW_FRAME} text-ink-1 ${MENU_ROW_LIT}`;
export const MENU_CHOICES_ROW = `${MENU_ROW} data-[state=open]:data-highlighted:bg-transparent`;
export const MENU_HEADING = "shrink-0 px-2 pt-2.5 pb-1 text-xs font-medium text-ink-3 first:pt-1.5";
export const MENU_GROUP_LABEL = "text-xs font-medium text-ink-3";
export const MENU_SEPARATOR = "-mx-1 my-1 h-px shrink-0 bg-edge";
export const MENU_NOTE = "shrink-0 px-2 py-1.5 text-sm text-ink-3";
export const MENU_SEARCH =
  "flex h-9 shrink-0 items-center gap-2 border-b border-edge px-3 text-ink-3";
export const MENU_SEARCH_INPUT =
  "h-full min-w-0 flex-1 bg-transparent text-base text-ink-1 placeholder:text-ink-3 focus:outline-none";
export const MENU_FOOTER =
  "flex shrink-0 items-center justify-between gap-3 border-t border-edge px-3.5 py-2";
export const SHEET_ROW = "flex items-center justify-between gap-4 text-base font-medium text-ink-2";
export const QUIET_ACTION = `inline-flex items-center gap-1.5 rounded-control text-sm text-ink-2 hover:text-ink-1 ${FOCUS_RING}`;

export const TIME_CHIP =
  "tabular grid h-12 w-16 place-items-center rounded-card border text-3xl font-semibold transition-colors duration-120";
export const TIME_CHIP_ON = "border-accent bg-accent/12 text-ink-1";
export const TIME_CHIP_OFF = "border-edge bg-surface-2 text-ink-2 hover:text-ink-1";

export const CHIP_FRAME = `rounded-pill border px-2.5 py-1 text-sm ${PRESS} ${TAP_AREA}`;
export const SCOPE_CHIP = `${CONTROL_HEIGHT} py-0`;
export const CHIP_ON = "border-edge-strong bg-surface-2 text-ink-1";
export const CHIP_OFF = "border-edge text-ink-2 hover:enabled:text-ink-1";

export const SWITCH_TRACK = "flex items-center gap-0.5 rounded-pill bg-surface-2 p-0.5";
export const SWITCH_PILL = `inline-flex h-5 items-center rounded-pill px-2 text-xs ${PRESS} ${TAP_AREA}`;
export const SWITCH_ON = "bg-surface-1 text-ink-1";
export const SWITCH_OFF = "text-ink-3 hover:enabled:text-ink-2";

export const CHOICE_FRAME = `rounded-card border bg-surface-1 ${PRESS_ROW}`;
export const CHOICE_ON = "border-accent ring-1 ring-accent";
export const CHOICE_OFF = "border-edge hover:border-edge-strong";
export const CHOICE_WELL = "border-b border-edge/60 bg-surface-0";

export const TILE_WELL = "h-36 border-b border-edge bg-surface-0 p-3";

export const SWATCH_EDGE = "outline -outline-offset-1 outline-ink-1/15";

export const CANVAS_PILL =
  "inline-flex h-7 items-center gap-1.5 rounded-pill border border-edge bg-surface-1/80 text-xs text-ink-2 backdrop-blur-sm";

export const CHART_TITLE = "min-w-0 truncate text-base text-ink-2";
export const CHART_FIGURE = "text-3xl leading-none font-semibold";
export const STAT_LABEL = "text-sm text-ink-3";
export const STAT_VALUE = "text-lg font-semibold tabular text-ink-1";

export const NOTICE = `flex gap-3 rounded-card ${CARD_PAD}`;
export const NOTICE_TONE = {
  neutral: "bg-surface-1",
  warning: "bg-status-warning/12",
  critical: "bg-status-critical/12",
} as const;

export const STATUS_TEXT = {
  good: "text-status-good",
  warning: "text-status-warning",
  critical: "text-status-critical",
  neutral: "text-ink-3",
} as const;
export const STATUS_FILL = {
  good: "bg-status-good",
  warning: "bg-status-warning",
  critical: "bg-status-critical",
  neutral: "bg-ink-3",
} as const;
export const OUTLOOK_TEXT = {
  good: STATUS_TEXT.good,
  bad: STATUS_TEXT.critical,
  neutral: STATUS_TEXT.neutral,
} as const;

export const PROGRESS_TRACK = "block h-1 w-full overflow-hidden rounded-pill bg-surface-2";
export const PROGRESS_FILL = "block h-full rounded-pill transition-[width] duration-500 ease-out";

export const PILL_FRAME = "rounded-pill border border-edge px-2.5 py-1 text-sm";
export const STATUS_PILL =
  "inline-flex max-w-full items-center gap-1.5 rounded-pill border border-dashed border-edge-strong bg-surface-1 px-2.5 py-1 text-sm text-ink-2";

export const KEYCAP =
  "inline-grid h-5 min-w-5 place-items-center rounded-sm border border-edge-strong bg-surface-1 px-1.5 font-sans text-2xs text-ink-2";
export const SKETCH_VEIL =
  "absolute inset-0 z-1 flex flex-col items-center justify-center gap-2 p-4 text-center whitespace-normal max-sm:justify-start max-sm:pt-6";

export const SQUIRCLE =
  "[mask-image:var(--mask-squircle)] [mask-size:100%_100%] [mask-repeat:no-repeat]";

export const PULSE_COLUMN = "w-full";

export const PLAN_PRICE =
  "text-[clamp(2.5rem,4.5vw,3.25rem)] leading-none font-semibold tracking-[-0.04em] text-ink-1";
